import {
  Card,
  ReviewLog,
  UserKnownWord,
  cleanCardNote,
  mergeTranslations,
  sanitizeTranslations,
} from '@lingoping/core';

/**
 * The rows of the synced tables (`cards`, `review_logs`, `user_known_words`) and the objects the
 * app works with, mapped both ways. Every real column is listed here, so a field added to the
 * `Card`/`ReviewLog`/`UserKnownWord` types never reaches the server by accident.
 *
 * Rows come from the `sync` Edge Function (services/syncEngine.ts) and, one last time per
 * installation, from the old merge (services/legacyMerge.ts).
 */

export const CARD_COLUMNS = [
  'id',
  'user_id',
  'target_language',
  'lemma',
  'front_text',
  'back_text',
  'part_of_speech',
  'linguistic_meta',
  'visual_meta',
  'image_url',
  'example_target',
  'example_native',
  'translations',
  'audio_url',
  'phonetic_ipa',
  'fsrs_state',
  'stability',
  'difficulty',
  'reps',
  'lapses',
  'last_review',
  'due_date',
  'queued_at',
  'queue_priority',
  'source_deck_id',
  'content_status',
  'retired_at',
  'note_front',
  'note_back',
  'notes_updated_at',
  'sense_key',
  'sense_hint',
  'created_at',
  'updated_at',
] as const;

export const REVIEW_LOG_COLUMNS = [
  'id',
  'card_id',
  'user_id',
  'rating',
  'state_before',
  'stability_after',
  'difficulty_after',
  'elapsed_days',
  'scheduled_days',
  'reviewed_at',
] as const;

export const KNOWN_WORD_COLUMNS = [
  'id',
  'user_id',
  'lemma',
  'language',
  'base_lemma',
  'created_at',
] as const;

/**
 * A moment as the app writes it ("2026-10-09T12:00:00.500Z"). The server answers the way
 * PostgreSQL writes moments ("2026-10-09T12:00:00.5+00:00"); parts of the app compare moments as
 * text (what is due, what was reviewed today), so every moment is brought into the one form on
 * its way into the saved copy. Anything that is not a moment is passed on as it is.
 */
export function isoMoment<T>(value: T): T | string {
  if (typeof value !== 'string') return value;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? new Date(ms).toISOString() : value;
}

export function cardToRow(card: Card, userId: string): Record<string, unknown> {
  return {
    id: card.id,
    user_id: userId,
    target_language: card.target_language,
    lemma: card.lemma ?? null,
    front_text: card.front_text,
    back_text: card.back_text ?? null,
    part_of_speech: card.part_of_speech ?? null,
    linguistic_meta: card.linguistic_meta ?? {},
    visual_meta: card.visual_meta ?? {},
    image_url: card.image_url ?? null,
    example_target: card.example_target ?? null,
    example_native: card.example_native ?? null,
    translations: card.translations ?? {},
    audio_url: card.audio_url ?? null,
    phonetic_ipa: card.phonetic_ipa ?? card.linguistic_meta?.phonetic_ipa ?? null,
    fsrs_state: card.fsrs_state,
    stability: card.stability,
    difficulty: card.difficulty,
    reps: card.reps,
    lapses: card.lapses,
    last_review: card.last_review,
    due_date: card.due_date,
    queued_at: card.queued_at ?? null,
    queue_priority: card.queue_priority === 1 ? 1 : 0,
    source_deck_id: card.source_deck_id ?? null,
    content_status: card.content_status === 'pending' ? 'pending' : 'ready',
    retired_at: card.retired_at ?? null,
    note_front: cleanCardNote(card.note_front),
    note_back: cleanCardNote(card.note_back),
    notes_updated_at: card.notes_updated_at ?? null,
    sense_key: card.sense_key ?? '',
    sense_hint: card.sense_hint ?? null,
    created_at: card.created_at,
    updated_at: card.updated_at,
  };
}

export function rowToCard(row: any): Card {
  return {
    id: row.id,
    user_id: row.user_id,
    target_language: row.target_language,
    lemma: row.lemma ?? undefined,
    front_text: row.front_text,
    back_text: row.back_text ?? undefined,
    part_of_speech: row.part_of_speech ?? undefined,
    phonetic_ipa: row.phonetic_ipa ?? undefined,
    linguistic_meta: row.linguistic_meta ?? undefined,
    visual_meta: row.visual_meta ?? undefined,
    image_url: row.image_url ?? undefined,
    example_target: row.example_target ?? undefined,
    example_native: row.example_native ?? undefined,
    translations: sanitizeTranslations(row.translations),
    audio_url: row.audio_url ?? undefined,
    fsrs_state: row.fsrs_state,
    stability: row.stability,
    difficulty: row.difficulty,
    reps: row.reps,
    lapses: row.lapses,
    last_review: isoMoment(row.last_review ?? null),
    due_date: isoMoment(row.due_date),
    queued_at: isoMoment(row.queued_at ?? null),
    queue_priority: row.queue_priority === 1 ? 1 : 0,
    source_deck_id: row.source_deck_id ?? null,
    content_status: row.content_status === 'pending' ? 'pending' : 'ready',
    retired_at: isoMoment(row.retired_at ?? null),
    note_front: cleanCardNote(row.note_front),
    note_back: cleanCardNote(row.note_back),
    notes_updated_at: isoMoment(row.notes_updated_at ?? null),
    sense_key: typeof row.sense_key === 'string' ? row.sense_key : '',
    sense_hint: row.sense_hint ?? null,
    created_at: isoMoment(row.created_at),
    updated_at: isoMoment(row.updated_at),
  };
}

export function reviewLogToRow(log: ReviewLog, userId: string): Record<string, unknown> {
  return {
    id: log.id,
    card_id: log.card_id,
    user_id: userId,
    rating: log.rating,
    state_before: log.state_before,
    stability_after: log.stability_after,
    difficulty_after: log.difficulty_after,
    elapsed_days: log.elapsed_days,
    scheduled_days: log.scheduled_days,
    reviewed_at: log.reviewed_at,
  };
}

export function rowToReviewLog(row: any): ReviewLog {
  return {
    id: row.id,
    card_id: row.card_id,
    user_id: row.user_id,
    rating: row.rating,
    state_before: row.state_before,
    stability_after: row.stability_after,
    difficulty_after: row.difficulty_after,
    elapsed_days: row.elapsed_days,
    scheduled_days: row.scheduled_days,
    reviewed_at: isoMoment(row.reviewed_at),
  };
}

export function rowToKnownWord(row: any): UserKnownWord {
  return {
    id: row.id,
    user_id: row.user_id,
    lemma: row.lemma,
    language: row.language,
    base_lemma: row.base_lemma ?? undefined,
    created_at: isoMoment(row.created_at),
  };
}

export function sameTranslations(a: Card['translations'], b: Card['translations']): boolean {
  return JSON.stringify(mergeTranslations(a)) === JSON.stringify(mergeTranslations(b));
}
